import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { useAppSelector } from '../app/hooks';
import { EventId } from '../services/types';

export enum VentureStatus {
  NOT_READY = 'NOT_READY',
  NOT_STARTED = 'NOT_STARTED',
  STARTED = 'STARTED',
  FINISHED = 'FINISHED',
}

export enum FinishType {
  Submission = 'submission',
  TimeOut = 'timeout',
}

interface VentureState {
  status: VentureStatus;
  finishType?: FinishType;
  expires?: string;
}

type VentureSliceState = {
  [k: string]: VentureState;
};

const initialState: VentureSliceState = {};

const ventureSlice = createSlice({
  name: 'venture',
  initialState,
  reducers: {
    setVentureNotReady(state, action: PayloadAction<{ eventId: EventId }>) {
      state[action.payload.eventId] = { status: VentureStatus.NOT_READY };
    },
    setVentureNotStarted(state, action: PayloadAction<{ eventId: EventId }>) {
      state[action.payload.eventId] = { status: VentureStatus.NOT_STARTED };
    },
    setVentureStarted(
      state,
      action: PayloadAction<{
        eventId: EventId;
        expires: string;
      }>,
    ) {
      state[action.payload.eventId] = {
        status: VentureStatus.STARTED,
        expires: action.payload.expires,
      };
    },
    setVentureFinished(
      state,
      action: PayloadAction<{ eventId: EventId; finishType?: FinishType }>,
    ) {
      const oldState = state[action.payload.eventId];
      state[action.payload.eventId] = {
        ...oldState,
        status: VentureStatus.FINISHED,
        finishType: action.payload.finishType,
      };
    },
    setVentureInfo(
      state,
      action: PayloadAction<{ eventId: EventId; ventureInfo: VentureState }>,
    ) {
      state[action.payload.eventId] = action.payload.ventureInfo;
    },
  },
});

export const {
  setVentureStarted,
  setVentureNotReady,
  setVentureNotStarted,
  setVentureFinished,
  setVentureInfo,
} = ventureSlice.actions;

export default ventureSlice.reducer;

export const useVentureExpiration = (eventId?: EventId): DateTime | undefined =>
  useAppSelector(
    (state) => {
      const expiresString = eventId && state.venture[eventId]?.expires;

      return expiresString ? DateTime.fromISO(expiresString) : undefined;
    },
    (previous, next) =>
      previous === undefined || next === undefined
        ? previous === next
        : previous.equals(next),
  );

export const useVentureFinish = (eventId?: EventId) =>
  useAppSelector((state) =>
    !eventId
      ? {}
      : {
          isFinished: state.venture[eventId]?.status === VentureStatus.FINISHED,
          finishType: state.venture[eventId]?.finishType,
        },
  );
